/**
 * Thin AVOWAL API client.
 *
 * In production the SPA is same-origin with the API (avowal.ai/api/* via CloudFront).
 * In dev, Vite proxies /api/* to the live API Gateway (see vite.config.ts).
 *
 * `submit()` posts the form, returns a discriminated result.
 */

export type FormKind = 'waitlist' | 'intake';

export type SubmitResult =
  | { ok: true; message: string }
  | { ok: false; error: string };

export async function submit(kind: FormKind, data: Record<string, unknown>): Promise<SubmitResult> {
  try {
    const res = await fetch(`/api/${kind}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...data, kind, source: 'avowal.ai', ts: new Date().toISOString() }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || body.ok === false) {
      return { ok: false, error: body.error ?? `HTTP ${res.status}` };
    }
    return {
      ok: true,
      message:
        body.message ??
        (kind === 'intake'
          ? 'Received. You will hear back within one business day.'
          : 'You are on the list. Welcome.'),
    };
  } catch (err) {
    return { ok: false, error: (err as Error).message };
  }
}

export interface LeadFields {
  email: string;
  jurisdiction?: string;
  matterType?: string;
  summary?: string;
  /** Any extra fields from a tenant's configured question set (posted as-is). */
  extra?: Record<string, string>;
}

/**
 * The plain-English structured summary the leads endpoint reflects back so the
 * prospect can confirm/correct it before seeing a quote (consumer-funnel §2.2).
 * The client may edit `facts` and re-submit; all fields optional (the API is a
 * sibling repo shipping in parallel — the flow synthesizes a summary locally
 * when the endpoint doesn't return one yet).
 */
export interface StructuredSummary {
  facts?: string;
  matterType?: string;
  jurisdiction?: string;
  county?: string;
  /** The tenant-config-driven intake answers, one per configured question. */
  fields?: { id: string; label: string; value: string }[];
}

/**
 * The anchored, NON-BINDING flat-fee estimate (consumer-funnel §2.2 + D0d band).
 * Pre-engagement the platform never asserts a binding legal price — `isEstimate`
 * is load-bearing. Carries the itemized covered / NOT-covered scope (CR3/CR4).
 * Either a band (low/high) or a point anchor may be present.
 */
export interface LeadQuote {
  legalFeeLowCents?: number;
  legalFeeHighCents?: number;
  legalFeeAnchorCents?: number;
  /** Client-facing legal fee (the platform fee bills to the attorney separately). */
  totalCents?: number;
  platformFeeCents?: number;
  currency: string;
  scope?: { covered?: string[]; notCovered?: string[] };
  slaHours?: number;
  rateCardKey?: string;
  isEstimate?: boolean;
}

export type LeadCaptureResult =
  | {
      ok: true;
      message: string;
      id?: string;
      status?: string;
      structuredSummary?: StructuredSummary;
      quote?: LeadQuote;
    }
  | { ok: false; error: string };

/**
 * Capture an intake into the real triage pipeline (avowal-api POST /leads).
 *
 * `submitted: true` graduates a complete form to a `new` intake that lands in
 * the staff queue (and alerts staff). `submitted: false` persists whatever the
 * prospect has entered as a `draft` so an abandoned form survives as a lead —
 * empty fields are dropped so a partial save never clobbers captured detail.
 */
export async function captureLead(fields: LeadFields, submitted: boolean): Promise<LeadCaptureResult> {
  try {
    const payload: Record<string, unknown> = { email: fields.email.trim(), source: 'avowal.ai', submitted };
    const j = fields.jurisdiction?.trim();
    const m = fields.matterType?.trim();
    const s = fields.summary?.trim();
    if (j) payload.jurisdiction = j;
    if (m) payload.matterType = m;
    if (s) payload.summary = s;
    // Extra tenant-configured fields ride along; empty values are dropped so a
    // partial save never clobbers captured detail.
    if (fields.extra) {
      for (const [k, v] of Object.entries(fields.extra)) {
        const t = v?.trim();
        if (t && !(k in payload)) payload[k] = t;
      }
    }

    const res = await fetch('/api/leads', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || body.ok === false) {
      return { ok: false, error: body.error ?? `HTTP ${res.status}` };
    }
    return {
      ok: true,
      id: typeof body.id === 'string' ? body.id : undefined,
      status: typeof body.status === 'string' ? body.status : undefined,
      structuredSummary: body.structuredSummary as StructuredSummary | undefined,
      quote: body.quote as LeadQuote | undefined,
      message:
        body.status === 'new'
          ? 'Received. Your intake is with the office — you\'ll hear back within one business day.'
          : 'Saved. Pick up where you left off whenever you\'re ready.',
    };
  } catch (err) {
    return { ok: false, error: (err as Error).message };
  }
}

/**
 * Save-and-resume read-back (consumer-funnel §3.2). Rehydrates a previously
 * saved intake's structured summary + quote, gated on the prospect's own email
 * (id + email must match — the prospect's own data only, nothing privileged).
 * Never throws; degrades to `ok:false` when the endpoint is absent.
 */
export type LeadQuoteResult =
  | { ok: true; structuredSummary?: StructuredSummary; quote?: LeadQuote; status?: string }
  | { ok: false; error: string };

export async function fetchLeadQuote(id: string, email: string): Promise<LeadQuoteResult> {
  try {
    const res = await fetch(`/api/leads/${encodeURIComponent(id)}/quote?email=${encodeURIComponent(email)}`, {
      headers: { Accept: 'application/json' },
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || body.ok === false) return { ok: false, error: body.error ?? `HTTP ${res.status}` };
    return {
      ok: true,
      status: typeof body.status === 'string' ? body.status : undefined,
      structuredSummary: body.structuredSummary as StructuredSummary | undefined,
      quote: body.quote as LeadQuote | undefined,
    };
  } catch (err) {
    return { ok: false, error: (err as Error).message };
  }
}
